import React from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { store } from './store/store';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
});

import Login from './components/auth/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ResetPassword from './pages/ResetPassword';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Messages from './pages/Messages';
import MyProfile from './pages/MyProfile';
import PublicProfile from './pages/PublicProfile';
import PublicProfiles from './pages/PublicProfiles';
import SocialPosts from './pages/SocialPosts';
import AddressBook from './pages/AddressBook';
import AccountHistory from './pages/AccountHistory';
import CategorySettings from './pages/CategorySettings';
import EarnCredits from './pages/EarnCredits';
import JointAccounts from './pages/JointAccounts';
import JointAccountDetail from './pages/JointAccountDetail';
import PaymentsSettingsPage from './pages/PaymentsSettingsPage';
import DonationSettings from './pages/DonationSettings';
import VerificationSettings from './pages/VerificationSettings';
import TrustHub from './pages/TrustHub';
import Layout from './components/layout/Layout';

const theme = createTheme({
  palette: {
    primary: {
      main: '#56903a', // 2Way MicroPay green
    },
    secondary: {
      main: '#2e7d32', // Darker green accent
    },
  },
});

const CREDENTIALING_WIZARD_URL = 'https://register.qixit.com/register';

const CredentialingExternalRedirect: React.FC = () => {
  React.useEffect(() => {
    window.location.replace(CREDENTIALING_WIZARD_URL);
  }, []);

  return <Navigate to="/messages" replace />;
};

const App: React.FC = () => {
  const useHashRouting =
    typeof window !== 'undefined' &&
    ['mail.qixit.com', 'stmail.qixit.com'].includes(window.location.hostname);
  React.useEffect(() => {
    if (!useHashRouting || typeof window === 'undefined') return;

    const { pathname, hash } = window.location;
    // Normalize malformed deep links like /login#/login and /register#/register.
    if (pathname === '/login' && hash === '#/login') {
      window.history.replaceState(null, '', '/#/login');
    } else if (pathname === '/register' && hash === '#/register') {
      window.history.replaceState(null, '', '/#/register');
    }
  }, [useHashRouting]);
  const RouterComponent = useHashRouting ? HashRouter : BrowserRouter;

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <RouterComponent>
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Navigate to="/messages" replace />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Messages />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Layout>
                    <MyProfile />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/:aliasName"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PublicProfile />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/public-profiles"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PublicProfiles />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/social-posts"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SocialPosts />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/address-book"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AddressBook />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/account-history"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AccountHistory />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/category-settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CategorySettings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/earn-credits"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EarnCredits />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PaymentsSettingsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/donation-settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DonationSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/verification"
              element={
                <ProtectedRoute>
                  <Layout>
                    <VerificationSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/joint-accounts"
              element={
                <ProtectedRoute>
                  <Layout>
                    <JointAccounts />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/joint-accounts/:jointAccountId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <JointAccountDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/joint-accounts/:jointAccountId/members"
              element={
                <ProtectedRoute>
                  <Layout>
                    <JointAccountDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/joint-accounts/:jointAccountId/history"
              element={
                <ProtectedRoute>
                  <Layout>
                    <JointAccountDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/joint-accounts/:jointAccountId/settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <JointAccountDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/credentialing"
              element={
                <ProtectedRoute>
                  <CredentialingExternalRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/credentialing/*"
              element={
                <ProtectedRoute>
                  <CredentialingExternalRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trust-hub"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TrustHub />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/messages" replace />} />
            </Routes>
          </RouterComponent>
        </ThemeProvider>
      </QueryClientProvider>
    </Provider>
  );
};

export default App;
